import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  Home,
  Calendar,
  Clock,
  Calculator,
  MapPin,
  Building2,
  History as HistoryIcon,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  DollarSign,
  TrendingUp,
  RefreshCw,
  Trash2
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/hooks/useAuth'
import { UnitHistoryTimeline } from '../components/UnitHistory/UnitHistoryTimeline'
import { PriceTrendChart } from '../components/UnitHistory/PriceTrendChart'
import { MetricCard } from '../components/UnitHistory/MetricCard'
import { UnitHistoryEvent, UnitHistoryEventType } from '../types/unit-history'

// Types
interface Unit {
  unit_id: number
  project_id: number
  unit_ref: string
  unit_url?: string
  typology?: string
  floor?: string
  fraction?: string
  status: 'available' | 'reserved' | 'sold' | 'unknown'
  available?: boolean
  private_gpa?: number
  extra_gpa?: number
  total_gpa?: number
  listing_price_1?: number
  listing_price_2?: number
  listing_price_3?: number
  current_price?: number
  parking?: number
  listing_date?: string
  sales_date?: string
  created_at: string
  updated_at?: string
  last_scraped_at?: string
  sqm_price?: number
  time_on_market?: number
  reserved_date?: string
  reservation_count?: number
  project: {
    project_id: number
    name: string
    address?: string
    city?: string
    district?: string
    website?: string
    architect?: { name: string; architect_id: number }
    developer?: { name: string; developer_id: number }
    broker?: { name: string; broker_id: number }
  }
}


// Utility function for consistent currency formatting
const formatCurrency = (amount: number) => {
  const formattedNumber = new Intl.NumberFormat('pt-PT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
  return `${formattedNumber} €`
}

export function UnitDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { canMutate } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const [editedUnit, setEditedUnit] = useState<Partial<Unit>>({})
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const queryClient = useQueryClient()

  // Fetch unit details
  const { data: unit, isLoading, error } = useQuery<Unit>({
    queryKey: ['unit', id],
    queryFn: () => api.get(`/units/${id}`).then(res => res.data),
    enabled: !!id
  })

  // Fetch unit history
  const { data: historyData, isLoading: historyLoading } = useQuery<{ history: UnitHistoryEvent[]; diffs: any[] }>({
    queryKey: ['unit-history', id],
    queryFn: () => api.get(`/units/${id}/history`).then(res => res.data),
    enabled: !!id
  })
  
  const history = historyData?.history || []

  // Update unit mutation
  const updateUnitMutation = useMutation({
    mutationFn: (data: Partial<Unit>) => {
      // Filter out fields that shouldn't be sent to the backend
      const { 
        project, 
        project_id,
        created_at, 
        updated_at, 
        last_scraped_at, 
        time_on_market, 
        unit_id,
        ...updateData 
      } = data as any
      
      
      // Ensure status is a valid enum value
      if (updateData.status && typeof updateData.status === 'string') {
        updateData.status = updateData.status as 'available' | 'reserved' | 'sold' | 'unknown'
      }
      
      // Available field is auto-managed by database trigger based on status
      // Remove it from update data to prevent manual editing
      delete updateData.available
      
      // Ensure numeric fields are numbers
      if (updateData.private_gpa !== undefined) {
        updateData.private_gpa = Number(updateData.private_gpa)
      }
      if (updateData.extra_gpa !== undefined) {
        updateData.extra_gpa = Number(updateData.extra_gpa)
      }
      if (updateData.total_gpa !== undefined) {
        updateData.total_gpa = Number(updateData.total_gpa)
      }
      if (updateData.current_price !== undefined) {
        updateData.current_price = Number(updateData.current_price)
      }
      if (updateData.parking !== undefined) {
        updateData.parking = Number(updateData.parking)
      }
      if (updateData.listing_price_1 !== undefined) {
        updateData.listing_price_1 = Number(updateData.listing_price_1)
      }
      if (updateData.listing_price_2 !== undefined) {
        updateData.listing_price_2 = Number(updateData.listing_price_2)
      }
      if (updateData.listing_price_3 !== undefined) {
        updateData.listing_price_3 = Number(updateData.listing_price_3)
      }
      if (updateData.sqm_price !== undefined) {
        updateData.sqm_price = Number(updateData.sqm_price)
      }
      if (updateData.reservation_count !== undefined) {
        updateData.reservation_count = Number(updateData.reservation_count)
      }
      
      // Remove time_on_market if it exists (it's a virtual field, not updatable)
      const { time_on_market: _, history: __, ...finalUpdateData } = updateData as any
      
      // Ensure date fields are properly formatted
      const dateFields = ['sales_date', 'listing_date', 'reserved_date']
      dateFields.forEach(field => {
        if (finalUpdateData[field]) {
          // Convert to ISO string if it's a date object, or ensure it's a valid date string
          const date = new Date(finalUpdateData[field])
          if (!isNaN(date.getTime())) {
            finalUpdateData[field] = date.toISOString().split('T')[0] // YYYY-MM-DD format
          } else {
            console.warn(`Invalid ${field} format:`, finalUpdateData[field])
            delete finalUpdateData[field]
          }
        }
      })
      
      console.log('Sending update data:', finalUpdateData)
      console.log('Sales date value:', finalUpdateData.sales_date)
      console.log('Sales date type:', typeof finalUpdateData.sales_date)
      return api.put(`/units/${id}`, finalUpdateData).then(res => res.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unit', id] })
      queryClient.invalidateQueries({ queryKey: ['unit-history', id] })
      toast.success('Unit updated successfully!')
      setIsEditing(false)
      setEditedUnit({})
    },
    onError: (error: any) => {
      console.error('Update error:', error)
      console.error('Error response:', error.response)
      console.error('Error data:', error.response?.data)
      console.error('Error status:', error.response?.status)
      console.error('Error message:', error.response?.data?.message)
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to update unit'
      
      toast.error(`Update failed: ${errorMessage}`)
    }
  })

  // Delete unit mutation
  const deleteUnitMutation = useMutation({
    mutationFn: () => api.delete(`/units/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['units'] })
      queryClient.invalidateQueries({ queryKey: ['project-units', unit?.project_id] })
      toast.success('Unit deleted successfully!')
      navigate('/units')
    },
    onError: (error: any) => {
      toast.error('Failed to delete unit. Please try again.')
      console.error('Delete error:', error)
    }
  })

  const handleEdit = () => {
    if (!unit) return
    
    // Exclude fields that shouldn't be editable
    const { 
      project, 
      project_id,
      created_at, 
      updated_at, 
      last_scraped_at, 
      time_on_market, 
      unit_id,
      available, // Auto-managed by database trigger
      ...editableUnit 
    } = unit
    
    setEditedUnit(editableUnit)
    setIsEditing(true)
  }

  const handleSave = async () => {
    if (!unit) return
    
    try {
      await updateUnitMutation.mutateAsync(editedUnit)
    } catch (error) {
      // Error is handled in the mutation
    }
  }

  const handleCancel = () => {
    setEditedUnit({})
    setIsEditing(false)
  }

  const handleFieldChange = (field: keyof Unit, value: any) => {
    setEditedUnit(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const getValue = (field: keyof Unit) => {
    return editedUnit[field] !== undefined ? editedUnit[field] : unit?.[field]
  }

  const handleDelete = () => {
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    try {
      await deleteUnitMutation.mutateAsync()
    } catch (error) {
      // Error is handled in the mutation
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
  }

  // Calculate metrics
  const priceTrend = React.useMemo(() => {
    return history
      ?.filter(h => h.eventType === UnitHistoryEventType.PRICE_CHANGE)
      .map(h => ({
        date: h.eventDate,
        price: h.price!,
        change: h.priceChangePercentage || 0,
        unitRef: unit?.unit_ref || ''
      })) || []
  }, [history, unit?.unit_ref])

  const metrics = React.useMemo(() => {
    if (!history || history.length === 0) return []

    const latestEvent = history[0]
    const priceChanges = history.filter(h => h.eventType === UnitHistoryEventType.PRICE_CHANGE).length
    const statusChanges = history.filter(h => h.eventType === UnitHistoryEventType.STATUS_CHANGE).length

    return [
      {
        title: 'Days on Market',
        value: unit?.time_on_market || 0,
        subtitle: 'Current listing period',
        icon: Clock
      },
      {
        title: 'Price Changes',
        value: priceChanges,
        subtitle: 'Total price updates',
        icon: DollarSign
      },
      {
        title: 'Status Changes',
        value: statusChanges,
        subtitle: 'Status updates',
        icon: CheckCircle
      },
      {
        title: 'Current Status',
        value: unit?.status || 'unknown',
        subtitle: 'Latest status',
        icon: CheckCircle
      }
    ]
  }, [history, unit])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-secondary-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-secondary-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error || !unit) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-16 w-16 text-danger-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-secondary-900 mb-2">Unit not found</h3>
        <p className="text-secondary-500 mb-4">The unit you're looking for doesn't exist or has been removed.</p>
        <button onClick={() => navigate('/units')} className="btn-primary">
          Back to Units
        </button>
      </div>
    )
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'badge-success'
      case 'reserved': return 'badge-warning'
      case 'sold': return 'badge-info'
      default: return 'badge-gray'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="btn-secondary flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">Unit {unit.unit_ref}</h1>
            <p className="text-secondary-600">{unit.project.name}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {unit.unit_url && (
            <button
              onClick={() => window.open(unit.unit_url, '_blank', 'noopener,noreferrer')}
              className="btn-secondary flex items-center"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Listing
            </button>
          )}
          {!isEditing ? (
            canMutate && (
              <>
                <button
                  onClick={handleEdit}
                  className="btn-primary flex items-center"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Unit
                </button>
                <button
                  onClick={handleDelete}
                  className="btn-danger flex items-center"
                  disabled={deleteUnitMutation.isPending}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Unit
                </button>
              </>
            )
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="btn-secondary flex items-center"
                disabled={updateUnitMutation.isPending}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="btn-primary flex items-center"
                disabled={updateUnitMutation.isPending}
              >
                <Save className="h-4 w-4 mr-2" />
                {updateUnitMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {metrics.map((metric, index) => (
          <MetricCard
            key={index}
            title={metric.title}
            value={metric.value}
            subtitle={metric.subtitle}
            icon={metric.icon}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Unit Details */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Unit Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">Unit Reference</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={String(getValue('unit_ref') || '')}
                    onChange={(e) => handleFieldChange('unit_ref', e.target.value)}
                    className="form-input"
                  />
                ) : (
                  <p className="text-secondary-900 font-medium">{unit.unit_ref}</p>
                )}
              </div>
              <div>
                <label className="form-label">Typology</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={String(getValue('typology') || '')}
                    onChange={(e) => handleFieldChange('typology', e.target.value)}
                    className="form-input"
                  />
                ) : (
                  <p className="text-secondary-900">{unit.typology || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="form-label">Floor</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={String(getValue('floor') || '')}
                    onChange={(e) => handleFieldChange('floor', e.target.value)}
                    className="form-input"
                  />
                ) : (
                  <p className="text-secondary-900">{unit.floor || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="form-label">Fraction</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={String(getValue('fraction') || '')}
                    onChange={(e) => handleFieldChange('fraction', e.target.value)}
                    className="form-input"
                  />
                ) : (
                  <p className="text-secondary-900">{unit.fraction || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="form-label">Status</label>
                {isEditing ? (
                  <select
                    value={String(getValue('status') || 'available')}
                    onChange={(e) => handleFieldChange('status', e.target.value)}
                    className="form-input"
                  >
                    <option value="available">Available</option>
                    <option value="reserved">Reserved</option>
                    <option value="sold">Sold</option>
                    <option value="unknown">Unknown</option>
                  </select>
                ) : (
                  <span className={`status-badge ${getStatusColor(unit.status)}`}>
                    {unit.status}
                  </span>
                )}
              </div>
              <div>
                <label className="form-label">Available</label>
                <p className="text-secondary-900">
                  {unit.available ? 'Yes' : 'No'}
                  <span className="text-xs text-secondary-500 ml-2">
                    (Auto-managed based on status)
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Property Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">Private GPA (m²)</label>
                {isEditing ? (
                  <input
                    type="number"
                    value={Number(getValue('private_gpa')) || ''}
                    onChange={(e) => handleFieldChange('private_gpa', parseFloat(e.target.value) || 0)}
                    className="form-input"
                  />
                ) : (
                  <p className="text-secondary-900">{Math.round(unit.private_gpa || 0)}m²</p>
                )}
              </div>
              <div>
                <label className="form-label">Extra GPA (m²)</label>
                {isEditing ? (
                  <input
                    type="number"
                    value={Number(getValue('extra_gpa')) || ''}
                    onChange={(e) => handleFieldChange('extra_gpa', parseFloat(e.target.value) || 0)}
                    className="form-input"
                  />
                ) : (
                  <p className="text-secondary-900">{Math.round(unit.extra_gpa || 0)}m²</p>
                )}
              </div>
              <div>
                <label className="form-label">Total GPA (m²)</label>
                {isEditing ? (
                  <input
                    type="number"
                    value={Number(getValue('total_gpa')) || ''}
                    onChange={(e) => handleFieldChange('total_gpa', parseFloat(e.target.value) || 0)}
                    className="form-input"
                  />
                ) : (
                  <p className="text-secondary-900">{Math.round(unit.total_gpa || 0)}m²</p>
                )}
              </div>
              <div>
                <label className="form-label">Parking</label>
                {isEditing ? (
                  <input
                    type="number"
                    value={Number(getValue('parking')) || ''}
                    onChange={(e) => handleFieldChange('parking', parseFloat(e.target.value) || 0)}
                    className="form-input"
                  />
                ) : (
                  <p className="text-secondary-900">{unit.parking || 0}</p>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Pricing Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">Current Price</label>
                {isEditing ? (
                  <input
                    type="number"
                    value={Number(getValue('current_price')) || ''}
                    onChange={(e) => handleFieldChange('current_price', parseFloat(e.target.value) || 0)}
                    className="form-input"
                  />
                ) : (
                  <p className="text-secondary-900 font-medium">{formatCurrency(unit.current_price || 0)}</p>
                )}
              </div>
              <div>
                <label className="form-label">Price/m²</label>
                <p className="text-secondary-900">
                  {(() => {
                    if (unit.sqm_price && typeof unit.sqm_price === 'number' && unit.sqm_price > 0) {
                      return `€${unit.sqm_price.toFixed(2)}/m²`
                    } else if (unit.current_price && unit.private_gpa && unit.private_gpa > 0) {
                      const calculatedPrice = unit.current_price / unit.private_gpa
                      return `€${calculatedPrice.toFixed(2)}/m²`
                    }
                    return 'N/A'
                  })()}
                </p>
              </div>
              <div>
                <label className="form-label">Original Price</label>
                <p className="text-secondary-900">{formatCurrency(unit.listing_price_1 || 0)}</p>
              </div>
              <div>
                <label className="form-label">Second Price</label>
                <p className="text-secondary-900">{unit.listing_price_2 ? formatCurrency(unit.listing_price_2) : 'N/A'}</p>
              </div>
              <div>
                <label className="form-label">Third Price</label>
                <p className="text-secondary-900">{unit.listing_price_3 ? formatCurrency(unit.listing_price_3) : 'N/A'}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Dates & Timeline</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">Listing Date</label>
                {isEditing ? (
                  <input
                    type="date"
                    value={getValue('listing_date') ? new Date(String(getValue('listing_date'))).toISOString().split('T')[0] : ''}
                    onChange={(e) => handleFieldChange('listing_date', e.target.value)}
                    className="form-input"
                  />
                ) : (
                  <p className="text-secondary-900">
                    {unit.listing_date ? new Date(String(unit.listing_date)).toLocaleDateString() : 'N/A'}
                  </p>
                )}
              </div>
              <div>
                <label className="form-label">Sales Date</label>
                {isEditing ? (
                  <input
                    type="date"
                    value={getValue('sales_date') ? new Date(String(getValue('sales_date'))).toISOString().split('T')[0] : ''}
                    onChange={(e) => handleFieldChange('sales_date', e.target.value)}
                    className="form-input"
                  />
                ) : (
                  <p className="text-secondary-900">
                    {unit.sales_date ? new Date(String(unit.sales_date)).toLocaleDateString() : 'N/A'}
                  </p>
                )}
              </div>
              <div>
                <label className="form-label">Last Scraped</label>
                <p className="text-secondary-900">
                  {unit.last_scraped_at ? new Date(String(unit.last_scraped_at)).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <label className="form-label">Days on Market</label>
                <p className="text-secondary-900 font-medium">{unit.time_on_market || 0} days</p>
              </div>
              <div>
                <label className="form-label">Reserved Date</label>
                <p className="text-secondary-900">
                  {unit.reserved_date ? new Date(String(unit.reserved_date)).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <label className="form-label">Reservation Count</label>
                <p className="text-secondary-900 font-medium">{unit.reservation_count || 0} times</p>
              </div>
            </div>
          </div>
        </div>

        {/* Project Information */}
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Project Information</h3>
            <div className="space-y-4">
              <div>
                <label className="form-label">Project Name</label>
                <button
                  onClick={() => navigate(`/projects/${unit.project.project_id}`)}
                  className="text-primary-600 hover:text-primary-700 font-medium text-left hover:underline transition-colors"
                >
                  {unit.project.name}
                </button>
              </div>
              <div>
                <label className="form-label">Address</label>
                <p className="text-secondary-900">{unit.project.address || 'N/A'}</p>
              </div>
              <div>
                <label className="form-label">Location</label>
                <p className="text-secondary-900">
                  {unit.project.city && unit.project.district 
                    ? `${unit.project.city}, ${unit.project.district}` 
                    : 'N/A'}
                </p>
              </div>
              {unit.project.website && (
                <div>
                  <label className="form-label">Website</label>
                  <a 
                    href={unit.project.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 flex items-center"
                  >
                    {unit.project.website}
                    <ExternalLink className="h-4 w-4 ml-1" />
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Associated Entities</h3>
            <div className="space-y-4">
              <div>
                <label className="form-label">Architect</label>
                {unit.project.architect ? (
                  <button
                    onClick={() => navigate(`/architects/${unit.project.architect.architect_id}`)}
                    className="text-primary-600 hover:text-primary-700 text-left hover:underline transition-colors"
                  >
                    {unit.project.architect.name}
                  </button>
                ) : (
                  <p className="text-secondary-700">Not assigned</p>
                )}
              </div>
              <div>
                <label className="form-label">Developer</label>
                {unit.project.developer ? (
                  <button
                    onClick={() => navigate(`/developers/${unit.project.developer.developer_id}`)}
                    className="text-primary-600 hover:text-primary-700 text-left hover:underline transition-colors"
                  >
                    {unit.project.developer.name}
                  </button>
                ) : (
                  <p className="text-secondary-700">Not assigned</p>
                )}
              </div>
              <div>
                <label className="form-label">Broker</label>
                {unit.project.broker ? (
                  <button
                    onClick={() => navigate(`/brokers/${unit.project.broker.broker_id}`)}
                    className="text-primary-600 hover:text-primary-700 text-left hover:underline transition-colors"
                  >
                    {unit.project.broker.name}
                  </button>
                ) : (
                  <p className="text-secondary-700">Not assigned</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Price Trend Chart */}
      {priceTrend.length > 0 && (
        <div className="bg-white p-6 rounded-xl shadow-tagus border border-secondary-100">
          <h3 className="text-lg font-semibold mb-4 text-secondary-900">Price Evolution</h3>
          <PriceTrendChart data={priceTrend} />
        </div>
      )}

      {/* Unit History Timeline */}
      <div className="bg-white p-6 rounded-xl shadow-tagus border border-secondary-100">
        <h3 className="text-lg font-semibold mb-4 text-secondary-900">Unit History</h3>
        <UnitHistoryTimeline events={history || []} />
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Delete Unit
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Are you sure you want to delete unit <strong>{unit?.unit_ref}</strong>? 
                  This action cannot be undone and will permanently remove all unit data and history.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={cancelDelete}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    disabled={deleteUnitMutation.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={deleteUnitMutation.isPending}
                  >
                    {deleteUnitMutation.isPending ? 'Deleting...' : 'Delete Unit'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}